/**
 * ResultsEditToolbar Component
 *
 * Subtle toolbar for query results edit mode controls.
 * Always shows a pencil button (disabled with reason when editing isn't possible),
 * and save/discard controls when in edit mode.
 */

import { Pencil, Save, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../components/ui/tooltip';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
type ResultsEditToolbarProps = {
  hasEditableColumns: boolean;
  readOnlyReason?: string;
  isEditMode: boolean;
  pendingChangeCount: number;
  isSaving: boolean;
  onToggleEditMode: () => void;
  onSave: () => void;
  onDiscard: () => void;
  onPreview?: () => void;
};

export function ResultsEditToolbar({
  hasEditableColumns,
  readOnlyReason,
  isEditMode,
  pendingChangeCount,
  isSaving,
  onToggleEditMode,
  onSave,
  onDiscard,
  onPreview,
}: ResultsEditToolbarProps) {
  if (!isEditMode) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={hasEditableColumns ? onToggleEditMode : undefined}
            disabled={!hasEditableColumns}
            className={`p-1.5 rounded-md transition-colors ${
              hasEditableColumns
                ? 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                : 'text-muted-foreground/40 cursor-not-allowed'
            }`}
            data-testid="results-edit-toggle"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {hasEditableColumns ? 'Edit results' : readOnlyReason ?? 'Results are read-only'}
        </TooltipContent>
      </Tooltip>
    );
  }

  const handleSaveOrPreview = onPreview && pendingChangeCount > 0 ? onPreview : onSave;

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={pendingChangeCount > 0 ? onPreview : undefined}
        className={`text-xs text-warning font-medium px-1 ${
          pendingChangeCount > 0 ? 'hover:underline cursor-pointer' : ''
        }`}
        data-testid="results-edit-label"
      >
        Edit
      </button>
      {pendingChangeCount > 0 && (
        <button
          type="button"
          onClick={onPreview}
          className="text-[10px] bg-amber-500/20 text-warning rounded-full px-1.5 min-w-[18px] text-center tabular-nums hover:bg-amber-500/30 transition-colors cursor-pointer"
          data-testid="results-edit-badge"
          title="Preview pending changes"
        >
          {pendingChangeCount}
        </button>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={handleSaveOrPreview}
            disabled={pendingChangeCount === 0 || isSaving}
            className="p-1 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-background/50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="results-edit-save"
          >
            {isSaving ? (
              <RandomDotmSpinner size={14} />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{onPreview ? 'Preview & save changes' : 'Save changes'}</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onDiscard}
            disabled={isSaving}
            className="p-1 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-background/50 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="results-edit-discard"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {pendingChangeCount > 0 ? 'Discard changes' : 'Exit edit mode'}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
